import type { UserProject } from '@teachur/http-client';

type ProjectStatus = UserProject['status'] | 'not_yet';

export const getProjectStatusBadgeClasses = (status: ProjectStatus) => {
  switch (status) {
    case 'pending':
      return {
        container: 'bg-(--primary-O300)',
        text: 'text-(--primary-O1100)',
      };

    case 'approved':
      return {
        container: 'bg-(--success-S200)',
        text: 'text-(--success-S700)',
      };

    case 'notYet':
    case 'not_yet':
      return {
        container: 'bg-(--danger-D300)/70',
        text: 'text-(--danger-D700)',
      };

    default:
      return {
        container: 'bg-(--neutral-white-NW600)',
        text: 'text-(--neutral-black-NB900)',
      };
  }
};

export const getClaimedByBadgeClasses = ({
  instructorId,
  currentUserId,
}: {
  instructorId?: number | null;
  currentUserId?: number;
}) => {
  if (instructorId == null) {
    return {
      container: 'border-(--primary-O1000)',
      text: 'text-(--primary-O1000)',
    };
  }

  if (instructorId === currentUserId) {
    return {
      container: 'border-(--secondary-B800)',
      text: 'text-(--secondary-B800)',
    };
  }

  return {
    container: 'border-(--neutral-black-NB500)',
    text: 'text-(--neutral-black-NB500)',
  };
};

export const getUserInitials = (firstName?: string, lastName?: string) => {
  const firstInitial = firstName?.trim().charAt(0).toUpperCase() ?? '';
  const lastInitial = lastName?.trim().charAt(0).toUpperCase() ?? '';

  return `${firstInitial}${lastInitial}` || '';
};

export const formatFullName = (firstName?: string, lastName?: string, fallback = 'Unknown') => {
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();

  return fullName || fallback;
};

export const formatCourseNames = (courses?: { name: string }[]) => {
  if (!courses?.length) return 'No course';

  return courses.map((course) => course.name).join(', ');
};

export const formatSubmittedDate = (date?: string) => {
  if (!date) return 'Unknown date';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
};

export const formatProjectStatus = (status?: ProjectStatus) => {
  switch (status) {
    case 'pending':
      return 'Pending';

    case 'inReview':
      return 'In review';

    case 'approved':
      return 'Approved';

    case 'notYet':
    case 'not_yet':
      return 'Not Yet';

    default:
      return 'Unknown';
  }
};

export const stripHtml = (html: string) => {
  return html
    .replace(/<style[^>]*>.*?<\/style>/gis, '')
    .replace(/<script[^>]*>.*?<\/script>/gis, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

export const formatProjectSummary = (description?: string, maxSentences = 2) => {
  if (!description) return 'No summary available.';

  const plainText = stripHtml(description);

  if (!plainText) return 'No summary available.';

  const sentences = plainText.split(/(?<=[.!?])\s+/).filter(Boolean);

  if (sentences.length <= maxSentences) {
    return plainText;
  }

  return `${sentences.slice(0, maxSentences).join(' ')}...`;
};
